import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Paper, Grid, IconButton,
    Card, CardContent, CardMedia, CardActions, Container, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
}));

const UploadBox = styled(Button)(({ theme }) => ({
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: '8px',
    padding: theme.spacing(1.5),
    textAlign: 'center',
    backgroundColor: '#fafafa',
    color: theme.palette.text.secondary,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    textTransform: 'none',
    transition: 'all 0.2s',
    minHeight: '80px',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#f0f7ff',
        color: theme.palette.primary.main,
    }
}));

const UpdateCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    boxShadow: 'none',
    transition: 'all 0.2s',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
        borderColor: theme.palette.primary.light,
    },
}));

const UpdatesAndTipsManagement = () => {
    const [updates, setUpdates] = useState([]);
    const [heading, setHeading] = useState('');
    const [content, setContent] = useState('');
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [message, setMessage] = useState({ type: '', text: '', open: false });
    const [mediaToDelete, setMediaToDelete] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => { },
    });

    const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

    useEffect(() => {
        fetchUpdates();
    }, []);

    const fetchUpdates = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const res = await axios.get(`${API_BASE_URL}/api/updates-tips`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUpdates(res.data);
        } catch (err) {
            console.error("Failed to fetch updates.", err);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setMediaFiles(prevFiles => [...prevFiles, ...files]);

            const newPreviews = files.map(file => ({
                url: URL.createObjectURL(file),
                type: file.type,
                name: file.name
            }));
            setMediaPreviews(prevPreviews => [...prevPreviews, ...newPreviews]);
        }
        e.target.value = null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!heading || !content) {
            setMessage({ type: 'error', text: 'Heading and Content are required.', open: true });
            return;
        }

        const formData = new FormData();
        formData.append('heading', heading);
        formData.append('content', content);

        if (mediaFiles && mediaFiles.length > 0) {
            mediaFiles.forEach((file) => {
                formData.append('media', file);
            });
        }

        if (editingId && mediaToDelete.length > 0) {
            formData.append('mediaToDelete', JSON.stringify(mediaToDelete));
        }

        try {
            const token = localStorage.getItem('jwtToken');
            if (editingId) {
                await axios.put(`${API_BASE_URL}/api/updates-tips/${editingId}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                setMessage({ type: 'success', text: 'Update modified successfully!', open: true });
            } else {
                await axios.post(`${API_BASE_URL}/api/updates-tips`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                setMessage({ type: 'success', text: 'Update posted successfully!', open: true });
            }

            handleCancelEdit();
            fetchUpdates();
        } catch (err) {
            console.error("Failed to save update.", err);
            setMessage({ type: 'error', text: 'Failed to save update.', open: true });
        }
    };

    const handleEditClick = (update) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Edit',
            message: 'Do you want to edit this update? It will be moved to the form above.',
            onConfirm: () => {
                setEditingId(update.id);
                setHeading(update.heading);
                setContent(update.content);
                setMediaFiles([]);
                setMediaToDelete([]);

                const existingMedia = update.media || (update.media_url ? [{ media_url: update.media_url, media_type: update.media_type }] : []);

                if (existingMedia.length > 0) {
                    setMediaPreviews(existingMedia.map(m => ({
                        url: m.media_url.startsWith('http') ? m.media_url : `${API_BASE_URL}/${m.media_url}`,
                        type: m.media_type === 'video' ? 'video/mp4' : 'image/jpeg',
                        isExisting: true
                    })));
                } else {
                    setMediaPreviews([]);
                }

                window.scrollTo({ top: 0, behavior: 'smooth' });
                setConfirmDialog({ ...confirmDialog, open: false });
            }
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setHeading('');
        setContent('');
        setMediaFiles([]);
        setMediaPreviews([]);
        setMediaToDelete([]);
    };

    const toggleExpanded = (id) => {
        setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleRemovePreview = (indexToRemove, preview) => {
        setConfirmDialog({
            open: true,
            title: 'Remove Media',
            message: 'Are you sure you want to remove this item from the upload list?',
            onConfirm: () => {
                if (preview.isExisting) {
                    const relativeUrl = preview.url.startsWith('http') ? preview.url : preview.url.replace(`${API_BASE_URL}/`, '');
                    setMediaToDelete(prev => [...prev, relativeUrl]);
                } else {
                    setMediaFiles(prevFiles => prevFiles.filter(file => {
                        try {
                            return URL.createObjectURL(file) !== preview.url;
                        } catch (e) { return true; }
                    }));
                }

                setMediaPreviews(prevPreviews => prevPreviews.filter((_, index) => index !== indexToRemove));

                setConfirmDialog({ ...confirmDialog, open: false });
            }
        });
    };

    const handleDelete = (id) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: 'Are you sure you want to delete this update? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem('jwtToken');
                    await axios.delete(`${API_BASE_URL}/api/updates-tips/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setMessage({ type: 'success', text: 'Update deleted successfully!', open: true });
                    fetchUpdates();
                    if (editingId === id) {
                        handleCancelEdit();
                    }
                } catch (err) {
                    console.error("Failed to delete update.", err);
                    setMessage({ type: 'error', text: 'Failed to delete update.', open: true });
                } finally {
                    setConfirmDialog({ ...confirmDialog, open: false });
                }
            }
        });
    };

    const handleCloseSnackbar = () => {
        setMessage({ ...message, open: false });
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 1, mb: 1, px: { xs: 2, sm: 3 } }}>
            <StyledPaper elevation={0}>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
                        {/* 1. Heading Input (Left Column - 3/12) */}
                        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <TextField
                                required
                                fullWidth
                                label="Heading / Title"
                                value={heading}
                                onChange={(e) => setHeading(e.target.value)}
                                placeholder="Enter title"
                                size="small"
                                
                            />
                        </Grid>
                            {/* 2. Content Input (Middle Column - FORCE WIDE) */}
                            <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{
                                flexGrow: 1,          // allow it to take more space
                                minWidth: 300,        // force wider box
                            }}
                            >
                            <TextField
                                required
                                fullWidth
                                multiline
                                rows={5}
                                label="Content"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Write detailed information here..."
                                size="small"
                                sx={{
                                width: '100%',      // ensure it fills grid
                                }}
                            />
                            </Grid>



                        {/* 3. Upload & Button (Right Column - 3/12) */}
                        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                height: '100%', 
                                justifyContent: 'space-between',
                                gap: 1 // Small gap between upload box and button
                            }}>
                                {/* Upload Box - Grows to fill space */}
                                <UploadBox component="label" sx={{
                                    flexGrow: 1, // This makes it stretch to fill available height
                                    width: '100%',
                                    border: '1px dashed #ccc',
                                    bgcolor: '#fafafa',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    // Make sure it looks clickable/active
                                    minHeight: 'unset' 
                                }}>
                                    <CloudUploadIcon sx={{ fontSize: 40, color: '#666', mb: 1 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                                        Click to Select Media
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {mediaFiles.length > 0 ? `${mediaFiles.length} file(s) selected` : 'Supported formats: Images/Videos'}
                                    </Typography>
                                    <input
                                        type="file"
                                        hidden
                                        multiple
                                        accept="image/*,video/*"
                                        onChange={handleFileChange}
                                    />
                                </UploadBox>

                                {/* Button - Stays at bottom */}
                                <Button
  type="submit"
  variant="contained"
  startIcon={<SaveIcon />}
  sx={{
    mt: 1,
    bgcolor: '#11224d',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    borderRadius: '8px',
    height: '40px',
    '&:hover': {
      bgcolor: '#0d1a3d',
    },
  }}
>
  POST UPDATE
</Button>

                            </Box>
                        </Grid>

                        {/* Media Previews (Full Width below the inputs) */}
                        {mediaPreviews.length > 0 && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                    {mediaPreviews.map((preview, index) => (
                                        <Box key={index} sx={{
                                            position: 'relative',
                                            width: 100,
                                            height: 100,
                                            borderRadius: 1,
                                            border: '1px solid #ddd',
                                            overflow: 'hidden',
                                            bgcolor: '#f5f5f5'
                                        }}>
                                            {(preview.type.startsWith('video') || preview.url.endsWith('.mp4') || preview.url.endsWith('.webm')) ? (
                                                <video src={preview.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <img src={preview.url} alt={`Preview ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemovePreview(index, preview)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                                                }}
                                            >
                                                <CloseIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </StyledPaper>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#11224d', mb: 2 }}>
                Manage Updates & Tips
            </Typography>
            <Grid container spacing={3}>
                {updates.length === 0 && (
                    <Grid item xs={12}>
                        <Typography variant="body1" color="textSecondary">No updates posted yet.</Typography>
                    </Grid>
                )}
                {updates.map((update) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={update.id}>
                        <UpdateCard>
                            {(() => {
                                const mediaList = update.media || (update.media_url ? [{ media_url: update.media_url, media_type: update.media_type }] : []);
                                if (mediaList.length === 0) return null;

                                return (
                                    <Box sx={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', '&::-webkit-scrollbar': { height: 4 } }}>
                                        {mediaList.map((media, idx) => (
                                            <Box key={idx} sx={{ minWidth: '100%', scrollSnapAlign: 'start', position: 'relative', height: 160 }}>
                                                {(media.media_type === 'video' || media.media_url.endsWith('.mp4') || media.media_url.endsWith('.webm')) ? (
                                                    <CardMedia
                                                        component="video"
                                                        controls
                                                        src={media.media_url.startsWith('http') ? media.media_url : `${API_BASE_URL}/${media.media_url}`}
                                                        sx={{ height: '100%', width: '100%', backgroundColor: '#000' }}
                                                    />
                                                ) : (
                                                    <CardMedia
                                                        component="img"
                                                        height="160"
                                                        image={media.media_url.startsWith('http') ? media.media_url : `${API_BASE_URL}/${media.media_url}`}
                                                        alt={update.heading}
                                                        sx={{ objectFit: 'cover', width: '100%' }}
                                                    />
                                                )}
                                                {mediaList.length > 1 && (
                                                    <Typography variant="caption" sx={{ position: 'absolute', bottom: 5, right: 5, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 1, borderRadius: 1 }}>
                                                        {idx + 1}/{mediaList.length}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                );
                            })()}
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                                    {update.heading}
                                </Typography>
                                {(() => {
                                    const isExpanded = expanded[update.id];
                                    const text = update.content || '';
                                    const shouldTruncate = text.length > 150;
                                    const displayContent = shouldTruncate && !isExpanded ? text.slice(0, 150) + '...' : text;
                                    return (
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {displayContent}
                                            </Typography>
                                            {shouldTruncate && (
                                                <Button size="small" onClick={() => toggleExpanded(update.id)} sx={{ mt: 0.5, p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 'bold' }}>
                                                    {isExpanded ? "Show Less" : "Show More"}
                                                </Button>
                                            )}
                                        </>
                                    );
                                })()}
                                <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.disabled' }}>
                                    Posted on: {new Date(update.created_at).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'flex-end' }}>
                                <IconButton aria-label="edit" color="primary" onClick={() => handleEditClick(update)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton aria-label="delete" color="error" onClick={() => handleDelete(update.id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </CardActions>
                        </UpdateCard>
                    </Grid>
                ))}
            </Grid>

            <Snackbar open={message.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={message.type} sx={{ width: '100%' }}>
                    {message.text}
                </Alert>
            </Snackbar>

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            >
                <DialogTitle>{confirmDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default UpdatesAndTipsManagement;